// Profile — candidate metadata. Reads/writes /api/profile (config/profile.yml).

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost } from '../lib/api';
import { friendlyExtractError } from '../lib/extract-errors';
import { useZh, useMultitenant } from '../lib/useProduct';
import type { UserProfile } from '../lib/types';

// GDPR right-to-erasure control. Two-click armed pattern (auto-reverts after
// 3s), hits DELETE /api/account with the confirm-query + confirm-header guard,
// then clears local state and bounces to /login. Only rendered in multi-tenant
// mode (single-user mode has no account to delete — the backend rejects it).
function DeleteAccountZone({ zh }: { zh: boolean }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);

  const onClick = async () => {
    if (busy) return;
    if (!armed) { setArmed(true); return; }
    setArmed(false);
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch('/api/account?confirm=true', {
        method: 'DELETE',
        headers: { 'X-Confirm-Destructive': 'yes' },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.message || j.error || `HTTP ${r.status}`);
      try { localStorage.clear(); } catch { /* private mode */ }
      window.location.href = '/login';
    } catch (e) {
      setErr((e instanceof Error && e.message) || (zh ? '删除失败，请重试。' : 'Could not delete. Try again.'));
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 26, marginTop: 24, border: '1px solid #fecaca', background: '#fff5f5' }}>
      <div className="serif" style={{ fontSize: 20, marginBottom: 8, color: '#991b1b' }}>{zh ? '删除账号' : 'Delete account'}</div>
      <div style={{ fontSize: 13, color: 'var(--ink3)', marginBottom: 16, lineHeight: 1.6 }}>
        {zh
          ? '永久删除你的账号和全部数据：简历、评估报告、职位匹配、申请记录。此操作不可撤销。'
          : 'Permanently delete your account and all data: CVs, evaluation reports, matches, applications. This cannot be undone.'}
      </div>
      {err && <div style={{ padding: 10, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13, marginBottom: 14, color: '#991b1b' }}>{err}</div>}
      <button
        onClick={onClick}
        disabled={busy}
        className="btn"
        style={{ background: armed ? '#dc2626' : 'transparent', color: armed ? '#fff' : '#dc2626', border: '1px solid #dc2626' }}
      >
        {busy ? (zh ? '删除中…' : 'Deleting…') : armed ? (zh ? '再点一次确认永久删除' : 'Click again to permanently delete') : (zh ? '删除我的账号' : 'Delete my account')}
      </button>
    </div>
  );
}

interface CVStatus { file: string | null; chars: number; preview?: string }

// Kicks off the cv_extract_profile job that asks Ollama to fill name/email/
// phone/etc. from the user's uploaded CV. Returns when the job *finishes*
// so the caller can refetch the profile and see the populated fields.
async function autofillFromCv(jobsTimeoutMs = 60_000): Promise<{ ok: boolean; reason?: string }> {
  const submit = await fetch('/api/jobs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cmd: 'cv_extract_profile', payload: '' }),
  });
  if (!submit.ok) return { ok: false, reason: `submit ${submit.status}` };
  const { job_id } = await submit.json();
  const t0 = Date.now();
  while (Date.now() - t0 < jobsTimeoutMs) {
    await new Promise((r) => setTimeout(r, 1500));
    const all = await fetch('/api/jobs').then((r) => r.json());
    const me = (all.jobs || []).find((j: { id: string }) => j.id === job_id);
    if (!me) return { ok: false, reason: 'job vanished' };
    if (me.status === 'done') return { ok: true };
    if (me.status === 'failed') return { ok: false, reason: 'job failed' };
  }
  return { ok: false, reason: 'timeout' };
}

// label = English (default product), zh = Chinese (UK-CN). Rendered per flag.
const FIELDS: { key: keyof UserProfile; label: string; zh: string; placeholder?: string }[] = [
  { key: 'name', label: 'Full name', zh: '姓名' },
  { key: 'headline', label: 'Headline', zh: '一句话简介' },
  { key: 'email', label: 'Email', zh: '邮箱' },
  { key: 'phone', label: 'Phone', zh: '电话' },
  { key: 'location', label: 'Location', zh: '所在城市' },
  { key: 'linkedin', label: 'LinkedIn URL', zh: 'LinkedIn 链接' },
  { key: 'targetComp', label: 'Target comp', zh: '期望薪资', placeholder: '£28–35K' },
  { key: 'minComp', label: 'Floor', zh: '最低可接受', placeholder: '£25K' },
  { key: 'narrative', label: 'Narrative', zh: '自我介绍' },
];

export default function Profile() {
  const zh = useZh();
  const multitenant = useMultitenant();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['profile'],
    queryFn: () => apiGet<UserProfile>('/api/profile'),
  });
  const cvStatusQ = useQuery({
    queryKey: ['cv-status'],
    queryFn: () => apiGet<CVStatus>('/api/cv-status'),
  });
  const [form, setForm] = useState<Partial<UserProfile>>({});
  const [saved, setSaved] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [autofillMsg, setAutofillMsg] = useState<string | null>(null);
  // Failure reason from cv_extract_profile (kept separate so the failure
  // card with retry buttons is sticky until the user acts on it — unlike
  // success/info messages which auto-clear).
  const [extractFailReason, setExtractFailReason] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (q.data) setForm(q.data);
  }, [q.data]);

  const mut = useMutation({
    mutationFn: (body: Partial<UserProfile>) => apiPost('/api/profile', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['profile-onboarded-check'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    },
  });

  const update = (k: keyof UserProfile, v: string) => setForm({ ...form, [k]: v });

  const isNewUser = q.data && q.data.onboarded === false;
  const hasCv = !!(cvStatusQ.data && cvStatusQ.data.chars && cvStatusQ.data.chars > 100);

  async function runExtract(): Promise<void> {
    setExtractFailReason(null);
    const r = await autofillFromCv(120_000);
    if (r.ok) {
      setAutofillMsg(zh ? '✓ 已从你的简历填好资料。核对下方内容后保存。' : '✓ Profile populated from your CV. Review below and Save when ready.');
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['cv-status'] });
      setTimeout(() => setAutofillMsg(null), 6000);
    } else {
      setAutofillMsg(null);
      setExtractFailReason(r.reason || 'job failed');
    }
  }

  async function handleUploadAndExtract(file: File) {
    setUploading(true);
    setAutofillMsg(zh ? `正在上传 ${file.name}…` : `Uploading ${file.name}…`);
    setExtractFailReason(null);
    try {
      const buf = await file.arrayBuffer();
      const res = await fetch('/api/upload', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Filename': encodeURIComponent(file.name),
        },
        body: buf,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      qc.invalidateQueries({ queryKey: ['cv-status'] });
      // Kick off the title-filter extraction in parallel — silent
      // best-effort; user can re-run from Settings → Title filter if
      // it doesn't complete. Without this, new tenants who upload via
      // Profile (not via /cv/upload) hit the scan gate with an empty
      // filter and have to manually click Re-extract.
      fetch('/api/title-filter/extract', { method: 'POST', credentials: 'include' })
        .catch(() => { /* swallow — surfaced separately on Settings/Title filter */ });
      setAutofillMsg(zh ? '简历已上传，正在提取你的信息（约 30 秒）…' : 'CV uploaded. Asking Ollama to fill in your details (≈30 s)…');
      await runExtract();
    } catch (e) {
      // Upload itself failed (network / 5xx). Treat as a transient
      // submit-style error so the same retry card renders.
      setAutofillMsg(null);
      setExtractFailReason(`submit ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  async function retryExtract() {
    setRetrying(true);
    await runExtract();
    setRetrying(false);
  }

  function saveAndComplete() {
    // Stamp onboarded:true on first save so the Shell stops bouncing
    // this user back here. Returning saves are no-ops for that flag.
    const body: Partial<UserProfile> = { ...form };
    if (isNewUser) body.onboarded = true;
    mut.mutate(body);
  }

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow={zh ? "你的资料 · 一切的基础" : "You · source of truth"}
        title={zh ? <>围绕 <em style={{ fontStyle: 'italic', color: 'var(--pri)' }}>你</em> 打造</> : <>Built around <em style={{ fontStyle: 'italic', color: 'var(--pri)' }}>you.</em></>}
        sub={zh ? "职位匹配、评估、定制简历都来自这里。填得越完整，推荐越精准。还没有英文简历？先去「生成简历」用中文生成一份。" : "Every match, evaluation, and tailored CV draws from here. The more you refine, the sharper every recommendation gets."}
        actions={
          <>
            <button className="btn" onClick={() => nav('/cv/upload')}>{zh ? "上传简历" : "Upload CV"}</button>
            {/* Manual auto-fill: re-run the CV → profile.yml extractor.
                Useful for users who onboarded before the auto-extract was
                wired in, or who want to refresh the contact block after
                editing their CV file. */}
            <button
              className="btn"
              disabled={autofilling || retrying}
              title={zh ? "让 AI 读取你上传的简历，自动填入姓名/邮箱/电话/所在地/LinkedIn/一句话简介。不会覆盖你已经填过的字段。" : "Ask Ollama to read your uploaded CV and fill in name/email/phone/location/LinkedIn/headline. Won't overwrite fields you've already typed."}
              onClick={async () => {
                setAutofilling(true);
                setAutofillMsg(zh ? '正在用 AI 解析你的简历…' : 'Parsing your CV with Ollama…');
                await runExtract();
                setAutofilling(false);
              }}
            >
              {autofilling ? (zh ? '解析中…' : 'Parsing…') : (zh ? '从简历自动填充' : 'Auto-fill from CV')}
            </button>
            <button className="btn" onClick={() => nav('/settings')} title={zh ? "扫描计划、职位源、通知" : "Scan schedule, portals, notifications"}>
              {zh ? "设置 →" : "Settings →"}
            </button>
            <button className="btn primary" disabled={mut.isPending} onClick={saveAndComplete}>
              {mut.isPending ? (zh ? '保存中…' : 'Saving…') : saved ? (zh ? '✓ 已保存' : '✓ Saved') : isNewUser ? (zh ? '保存并完成设置' : 'Save & finish setup') : (zh ? '保存修改' : 'Save changes')}
            </button>
          </>
        }
      />
      {autofillMsg && !extractFailReason && (
        <div
          style={{
            margin: '0 0 14px',
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid var(--line)',
            background: autofillMsg.startsWith('✓') ? 'var(--pri-soft)' : 'var(--surface)',
            color: autofillMsg.startsWith('✓') ? 'var(--pri)' : 'var(--ink2)',
            fontSize: 13,
          }}
        >
          {autofillMsg}
        </div>
      )}

      {/* Sticky failure card with retry — replaces the old auto-clearing
          autofillMsg for failure cases. Stays until user takes action. */}
      {extractFailReason && (() => {
        const f = friendlyExtractError(extractFailReason);
        return (
          <div
            style={{
              margin: '0 0 14px',
              padding: '14px 16px',
              borderRadius: 10,
              border: '1px solid #fca5a5',
              background: '#fef2f2',
              color: '#7f1d1d',
              fontSize: 13,
              lineHeight: 1.55,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 16 }}>⚠️</span>
              <strong style={{ fontSize: 14 }}>{f.title}</strong>
            </div>
            <div style={{ marginBottom: 4 }}>{f.body}</div>
            <div style={{ fontSize: 11, color: '#a16363', marginBottom: 12 }}>
              raw reason: <code>{extractFailReason}</code>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn primary"
                disabled={retrying || autofilling || uploading}
                onClick={retryExtract}
              >
                {retrying ? 'Retrying…' : 'Retry parse'}
              </button>
              <button
                className="btn"
                disabled={retrying || autofilling || uploading}
                onClick={() => fileRef.current?.click()}
              >
                Upload different file
              </button>
              <button
                className="btn ghost"
                disabled={retrying || autofilling || uploading}
                onClick={() => setExtractFailReason(null)}
              >
                Dismiss — fill manually
              </button>
            </div>
          </div>
        );
      })()}

      {/* CV-upload guide banner. Shown when:
          - new tenant (onboarded=false), regardless of CV state, or
          - returning tenant who hasn't uploaded a CV yet.
          The copy adapts to which case is active so the banner reads
          like a personal nudge, not a generic empty state. Hidden once
          a returning tenant has both onboarded and uploaded a CV. */}
      {(isNewUser || (q.data && !hasCv && !cvStatusQ.isLoading)) && (
        <div
          style={{
            margin: '0 0 22px',
            padding: 24,
            borderRadius: 12,
            border: '2px dashed var(--pri)',
            background: 'var(--pri-soft)',
          }}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer?.files?.[0];
            if (f && !uploading) handleUploadAndExtract(f);
          }}
        >
          <div className="eyebrow" style={{ color: 'var(--pri)', marginBottom: 6 }}>
            {isNewUser
              ? (zh ? '欢迎——2 分钟完成设置' : "Welcome — let's set you up in 2 minutes")
              : (zh ? '提示 · 我们还没有你的简历' : "Heads up · we don't have your CV yet")}
          </div>
          <h2 className="serif" style={{ fontSize: 28, fontWeight: 400, margin: '4px 0 10px', letterSpacing: '-0.01em' }}>
            {hasCv
              ? (zh ? '简历已就绪。核对下方信息后保存。' : 'CV looks ready. Review your details below and save.')
              : isNewUser
                ? (zh ? '上传简历，其余交给我们。' : "Drop your CV — we'll fill in the rest.")
                : (zh ? '上传简历以解锁定制匹配。' : 'Upload your CV to unlock tailored matches.')}
          </h2>
          <p style={{ color: 'var(--ink2)', fontSize: 14.5, lineHeight: 1.55, margin: '0 0 16px', maxWidth: 640 }}>
            {hasCv
              ? (zh ? '下面是我们提取的联系方式。有错就改，然后点「保存并完成设置」。之后随时可以回来完善。' : "Below are the contact details we extracted. Fix any field that's wrong, then click Save & finish setup. You can come back any time to refine.")
              : isNewUser
                ? (zh ? '把 PDF、MD 或 TXT 文件拖到这张横幅上——或点按钮。系统会读取并提取姓名 / 邮箱 / 电话 / LinkedIn / 所在地，预填下方表单，约 30 秒。还没有英文简历？先去「生成简历」用中文生成一份。' : "Drag a PDF, MD, or TXT file anywhere on this banner — or click the button. We'll send it to your private Ollama instance, pull out name / email / phone / LinkedIn / location, and pre-fill the form below. Takes about 30 seconds.")
                : (zh ? '没有简历时，评估和定制会对每个职位做零样本盲读，评分更粗糙、简历更笼统。上传一次，之后每次评估都会个性化。' : 'Without a CV, evaluate and tailor commands fall back to a zero-shot blind read of every job description, which gives you noisier scores and generic CVs. Upload it once and every future evaluation is personalised.')}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.md,.txt,.docx"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUploadAndExtract(f);
              }}
            />
            <button
              className="btn accent"
              disabled={uploading || autofilling}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? (zh ? '上传中…' : 'Uploading…') : autofilling ? (zh ? '提取中…' : 'Extracting…') : hasCv ? (zh ? '更换简历' : 'Replace CV') : (zh ? '选择简历文件' : 'Choose CV file')}
            </button>
            {hasCv && (
              <span style={{ color: 'var(--ink2)', fontSize: 13 }}>
                {zh ? "当前：" : "Current: "}<code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>{cvStatusQ.data?.file?.split('/').pop() ?? 'cv'}</code>
                {' '}({cvStatusQ.data?.chars} chars)
              </span>
            )}
          </div>
          {/* The English copy below is the self-hosted product's, where "your
              home Ollama" is literally the user's own machine. In the hosted
              UK-CN product it is OUR hardware, so the Chinese text says that
              instead — the claim that matters (never a third-party LLM API) is
              true either way, but "stays on your machine" would not be. */}
          <div style={{ marginTop: 14, fontSize: 12, color: 'var(--ink3)' }}>
            {zh
              ? '简历只保存在你自己的账号空间，解析时发送到 Nice2Job 自建的模型服务器，不会经过 OpenAI、Anthropic 等任何第三方大模型服务。'
              : 'Your CV stays on your private dataspace and is only sent to your home Ollama for parsing — never to a third-party LLM.'}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 20 }}>
        <div className="card" style={{ padding: 26 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div className="avatar" style={{ width: 60, height: 60, fontSize: 20 }}>
              {form.initials || (form.name ? form.name.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase() : '?')}
            </div>
            <div>
              <div className="serif" style={{ fontSize: 24 }}>{form.name || 'Unnamed'}</div>
              <div style={{ fontSize: 12.5, color: 'var(--ink3)' }}>{form.headline || '—'}</div>
            </div>
          </div>
          <div className="hair" style={{ margin: '20px 0' }} />
          {FIELDS.map((f) => (
            <div key={f.key} style={{ marginBottom: 14 }}>
              <div className="tab" style={{ marginBottom: 6 }}>{zh ? f.zh : f.label}</div>
              {f.key === 'narrative' ? (
                <textarea
                  value={String(form[f.key] ?? '')}
                  onChange={(e) => update(f.key, e.target.value)}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder={f.placeholder}
                />
              ) : (
                <input
                  value={String(form[f.key] ?? '')}
                  onChange={(e) => update(f.key, e.target.value)}
                  style={inputStyle}
                  placeholder={f.placeholder}
                />
              )}
            </div>
          ))}
        </div>
        <div>
          <div className="card" style={{ padding: 26, marginBottom: 16 }}>
            <div className="serif" style={{ fontSize: 22, marginBottom: 4 }}>{zh ? "目标岗位" : "Target roles"}</div>
            <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 18 }}>
              {zh ? "逗号分隔。每个职位匹配都会围绕这些优化。" : "Comma-separated. We optimize every match against these."}
            </div>
            <textarea
              value={(form.targetRoles || []).join(', ')}
              onChange={(e) =>
                setForm({
                  ...form,
                  targetRoles: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                })
              }
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder={zh ? "投行分析师、数据分析师、市场营销实习生" : "Senior AI Engineer, Staff ML Engineer"}
            />
          </div>
          <div className="card" style={{ padding: 26 }}>
            <div className="serif" style={{ fontSize: 20, marginBottom: 10 }}>{zh ? "你的优势" : "Superpowers"}</div>
            <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 14 }}>{zh ? "逗号分隔。用于简历定制和求职信。" : "Comma-separated. Used in CV tailoring + cover letters."}</div>
            <textarea
              value={(form.superpowers || []).join(', ')}
              onChange={(e) =>
                setForm({
                  ...form,
                  superpowers: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                })
              }
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder={zh ? "财务建模、双语沟通、数据分析" : "End-to-end ML pipelines, Fast prototyping · 2-week ship"}
            />
          </div>
        </div>
        {multitenant && <DeleteAccountZone zh={zh} />}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8,
  fontSize: 13, fontFamily: 'Inter, var(--cjk-sans), sans-serif', background: 'var(--bg)',
};
